/**
 * Use store is a client specific component to use states around components
 * Here is our global state to use around
 * To import and use the component
 * import useStore from '../core/useStore'
 * To use in compoents
 * const useId = useStore((state) => state.useId)
 * direct call useStore.getState().username
 * to set by components
 * const useId = (value) => useStore.setState({useId: value})
 * We can also initiate more functions in this component for basic use like popups and errors
 *
 */
import { create } from 'zustand';
import { getMdTitle, getMdDescription } from './markDownReader';
import { v4 as uuidv4 } from 'uuid';
import { regenTokenifExpired } from '../helpers/utilityFunctions';
/* eslint-disable */
const contentPath = process.env.REACT_APP_CONTENT_SERVER || '/api-content';
const useStore = create((set) => ({
  healthCheckStatus: false,
  useId: 0,
  token: '',
  error: '',
  loggedIn: '',
  currentUser: [],
  roles: [],
  username: '',
  accessToken: '',
  email: '',
  apps: [],
  status: 0,
  catalogItems: [],
  apiSideMenu: [],
  apiSideMenuResolved: false,
  defaultApiSideMenu: [],
  versionCheckItems: [],
  menuAvailable: [],
  apisVersionControl: [],
  faqsItems: [],
  defaultCatalogItems: [],
  // Private per User APIs
  userPassTok: '',
  userServerTok: '',
  userUserTok: '',
  userBasicTok: '',
  userBearerTok: '',
  cardIconsMapping: [],
  searchResourcesMock: [],
  customApiWeightJson: [],
  integratedSolData: {
    cardsConfig: null,
    cardsLogoMap: {},
    sectionsLogoMap: {},
    loading: false,
    initialized: false,
    error: null,
  },
  // updatecatalogData: (data) => set(state => ({ getCatalogItems: data })),
  // more user specific variables can be used here and shared with components around
  action: () => {
    // const getCatalogItems = get().catalogItems;
  },
}));

/* eslint-enable */
//  Use Store helpers that doesn't depend on Data from UseStore here
// Slug / Path from title creator
const slugCreator = (title) => {
  try {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  } catch (error) {
    return;
  }
};

//  Update resources Links to sync with our content server
const updateResources = (data, path, resourceType) => {
  if (resourceType === 'apis') {
    const results = data
      .replaceAll(
        '](resources/',
        `](${contentPath}/portal-content?resourcePath=${path}/docs/bizcontent/resources&resourceName=`,
      )
      .toString();
    return results;
  } else {
    const results = data
      .replaceAll('](res', `](${contentPath}/portal-content?resourcePath=${path}&resourceName=/res`)
      .toString();
    return results;
  }
};

//  Update resources Links to sync with our content server and download
const filesResources = (path, filename) => {
  return `${contentPath}/portal-content?resourcePath=${path}&resourceName=${filename}`;
};

//  Download file from URL request
function downloadURI(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
}

// Create Items for table of contents from MD file
const createContentsTable = (data) => {
  //   From Header example
  //  Get titles from MD
  const titles = getMdTitle(data);
  const results = [];
  titles.length > 0 &&
    titles.forEach((x) => {
      results.push({ displayText: x, id: `${slugCreator(x)}` });
    });
  return results;
};
/**
 *
 * @param {markdown || string} data
 * @returns
 */
function addIdsToMddata(data = '') {
  let after = data.split('\n');
  const titles = getMdTitle(data);
  titles.length > 0 &&
    titles.forEach((x) => {
      let index = after.indexOf(x);
      after[index] = `## ${x}`;
    });
  return after.join('\n');
}

// Create Items for table of contents from MD file
const getMDAsArray = (data) => {
  //   From Header example
  //  Get titles from MD
  const titles = getMdDescription(data);
  const results = [];
  titles.length > 0 &&
    titles.forEach((x) => {
      results.push({ displayText: x, id: `${slugCreator(x)}` });
    });
  return results;
};
// @todo: Dharama please fix the content component as what we did in API core and also must accept then as well,
// so you don't close it within the content component please
const contentFetch = async (url) => {
  // const token = localStorage.getItem('token');
  const token = await regenTokenifExpired();
  return await fetch(url, {
    headers: {
      Authorization: token,
      uuid: uuidv4(),
      audience: 'public',
    },
    credentials: 'omit',
  })
    .then(async (x) => {
      if (x.status !== 200 || x.status !== 201) {
        return await fallBackRecall(url, x.status);
      } else {
        try {
          const data = await x.json();
          JSON.parse(data);
          return {
            type: 'success',
            response: data,
          };
        } catch (error) {
          const data = await x.text();
          return {
            type: 'success',
            response: data,
          };
        }
      }
    })
    // Fallback process
    .catch(async (err) => {
      return {
        type: 'fail',
        response: err,
      };
    });
};

// Fallbacks recall
const fallBackRecall = async (url = '', err = 0) => {
  // /api-content/portal-content?resourcePath=pages&resourceName=homepage-testimonials.json
  const fallBackUrl = url
    .replace(contentPath + '/portal-content?resourcePath=', '/assets/fallbacks/')
    .replace('&resourceName=', '/');
  return await fetch(fallBackUrl)
    .then(async (x) => {
      let data;
      console.log(
        url +
          ' API is down with code ' +
          err +
          ', Fallback from ' +
          fallBackUrl +
          ' is fetching now',
      );
      try {
        data = await x.json();
      } catch (error) {
        data = await x.text();
      }
      return {
        type: 'success',
        response: data || x,
      };
    })
    .catch((err) => {
      return {
        type: 'fail',
        response: err,
      };
    });
};

function handleVersionControl(apiMachineName, catalogItems, versionValue) {
  // Find index of current catalog item from the catalog list
  let selectedItem = catalogItems?.findIndex(
    (i) => i.name === apiMachineName && i.version === versionValue,
  );

  // Slice the menu items
  let updatedCatalogItems = [
    ...catalogItems.slice(0, selectedItem),
    ...catalogItems.slice(selectedItem + 1),
  ];

  //Filter the duplicates
  updatedCatalogItems = [
    ...new Map(updatedCatalogItems.map((item) => [item['api_display_name'], item])).values(),
  ];
  return updatedCatalogItems;
}

//to add delay for code execution
const useTimeoutdelay = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

// eslint-disable-next-line no-unused-vars
/**
 * consoleLog is the place to log for development logs, this would log as warning to be clear
 * to enable it you must enable the REACT_APP_LOGGING in ENV base
 * @param {*} text
 * @returns
 */
function consoleLog(...text) {
  if (process.env.REACT_APP_LOGGING === 'true' || process.env.REACT_APP_LOGGING === true) {
    return new Promise(() => console.warn(text));
  }
  return;
}

export {
  useStore,
  slugCreator,
  updateResources,
  createContentsTable,
  filesResources,
  contentPath,
  downloadURI,
  getMDAsArray,
  addIdsToMddata,
  contentFetch,
  fallBackRecall,
  handleVersionControl,
  useTimeoutdelay,
  consoleLog,
};
